import React, { Suspense, lazy, useEffect } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { store, RootState } from './store'
import { setUser, setLoading } from './store/authSlice'
import authService from './services/authService'
import RootRoute from './components/RootRoute'
import PageFallback from './components/PageFallback'
import ProtectedRoute from './components/ProtectedRoute'
import Navigation from './components/Navigation'
import ScrollToTop from './components/ScrollToTop'
import { ThemeProvider } from './hooks/use-theme'
import { LanguageProvider } from './hooks/use-i18n'

const Login = lazy(() => import('./pages/Login'))
const Signup = lazy(() => import('./pages/Signup'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Progress = lazy(() => import('./pages/Progress'))
const StudyPlan = lazy(() => import('./pages/StudyPlan'))
const Learn = lazy(() => import('./pages/Learn'))
const AcademRoom = lazy(() => import('./pages/AcademRoom'))
const SoloPlay = lazy(() => import('./pages/SoloPlay'))
const WeeklyBoard = lazy(() => import('./pages/WeeklyBoard'))
const Mistakes = lazy(() => import('./pages/Mistakes'))
const CompanyRegistration = lazy(() => import('./pages/CompanyRegistration'))
const CompanyRegistrationSuccess = lazy(() => import('./pages/CompanyRegistrationSuccess'))
const Arena = lazy(() => import('./pages/Arena'))
const Golf = lazy(() => import('./pages/Golf'))
const GolfRoom = lazy(() => import('./pages/GolfRoom'))
const GolfPlay = lazy(() => import('./pages/GolfPlay'))
const Showcase = lazy(() => import('./pages/Showcase'))
const Promo = lazy(() => import('./pages/Promo'))
const AboutUs = lazy(() => import('./pages/AboutUs'))
const Demo = lazy(() => import('./pages/Demo'))
const Rankings = lazy(() => import('./pages/Rankings'))
const Room = lazy(() => import('./pages/Room'))
const Predictions = lazy(() => import('./pages/Predictions'))
const FutureChampionPlay = lazy(() => import('./pages/FutureChampionPlay'))
const InviteLanding = lazy(() => import('./pages/InviteLanding'))
const Invitations = lazy(() => import('./pages/Invitations'))
const Formula1Room = lazy(() => import('./pages/Formula1Room'))
const Formula1Race = lazy(() => import('./pages/Formula1Race'))
const Formula1Playing = lazy(() => import('./pages/Formula1Playing'))
const ChessRoom = lazy(() => import('./pages/ChessRoom'))
const ChessArenaRoom = lazy(() => import('./pages/ChessArenaRoom'))
const ChessArenaGameReview = lazy(() => import('./pages/ChessArenaGameReview'))
const ChessGame = lazy(() => import('./pages/ChessGame'))
const PublicHub = lazy(() => import('./pages/PublicHub'))
const PublicLeaderboard = lazy(() => import('./pages/PublicLeaderboard'))
const PredictionBoard = lazy(() => import('./pages/PredictionBoard'))
const PublicFootballPage = lazy(() => import('./pages/PublicFootballPage'))
const Account = lazy(() => import('./pages/Account'))
const PublicFormula1Page = lazy(() => import('./pages/PublicFormula1Page'))
const PublicFormula1PlayPage = lazy(() => import('./pages/PublicFormula1PlayPage'))
const AdminUpdateResults = lazy(() => import('./pages/AdminUpdateResults'))
const AdminSocialPosters = lazy(() => import('./pages/AdminSocialPosters'))
const AdminMissingPredictions = lazy(() => import('./pages/AdminMissingPredictions'))
const AdminAgentAnalytics = lazy(() => import('./pages/AdminAgentAnalytics'))
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute - reuse cache when revisiting room
      refetchOnWindowFocus: false,
    },
  },
})

// Component to handle authentication restoration
const AuthInitializer: React.FC = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { user, token } = authService.restoreAuth()
        if (user && token) {
          // Optimistically restore from localStorage so the UI is instant,
          // then validate against the server (same pattern as mobileBrc).
          dispatch(setUser(user))
          try {
            const freshUser = await authService.getCurrentUser()
            // Keep localStorage in sync with the latest user data from server
            authService.setAuthData(freshUser, token)
            dispatch(setUser(freshUser))
          } catch {
            // Server validation failed (expired/revoked token) — log out cleanly
            authService.logout()
            dispatch(setUser(null))
          }
        }
      } catch (error) {
        console.error('Auth restoration failed:', error)
      } finally {
        dispatch(setLoading(false))
      }
    }

    initializeAuth()
  }, [dispatch])

  return null
}

/** Top offset for the fixed Navigation — skip only on the guest landing. */
const MainContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation()
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)
  const hideNav =
    (pathname === '/' && !isAuthenticated) || pathname === '/invite' || pathname === '/demo'
  return (
    <div
      className={hideNav ? '' : 'pt-[var(--academ-nav-offset)]'}
    >
      {children}
    </div>
  )
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
        <Router>
          <ScrollToTop />
          <AuthInitializer />
          <div className="min-h-screen bg-[#E6F4EC] text-foreground overflow-x-hidden">
          <Navigation />
          <MainContent>
            <Suspense fallback={<PageFallback />}>
            <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/invite" element={<InviteLanding />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/solos"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/shared"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn/mistakes"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/improve"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/improve/mistakes"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/improve/mistakes/:subject"
              element={
                <ProtectedRoute>
                  <Mistakes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/improve/topics"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn/subject"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn/subject/:subject"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn/subject/:subject/:topicSlug"
              element={
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <Progress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress/plan"
              element={
                <ProtectedRoute>
                  <StudyPlan />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academ/:roomId"
              element={
                <ProtectedRoute>
                  <AcademRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/solo/:subject"
              element={
                <ProtectedRoute>
                  <SoloPlay />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mistakes/:subject"
              element={
                <ProtectedRoute>
                  <Mistakes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/weekly"
              element={
                <ProtectedRoute>
                  <WeeklyBoard />
                </ProtectedRoute>
              }
            />
            {/* Legacy sports routes kept temporarily — not linked from Academ nav */}
            <Route path="/arena" element={<Arena />} />
            <Route path="/golf" element={<Golf />} />
            <Route
              path="/golf/:roomId"
              element={
                <ProtectedRoute>
                  <GolfRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/golf/:roomId/play"
              element={
                <ProtectedRoute>
                  <GolfPlay />
                </ProtectedRoute>
              }
            />
            <Route path="/news" element={<Navigate to="/progress" replace />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/promo" element={<Promo />} />
            <Route path="/promo/seq1" element={<Promo />} />
            <Route path="/demo/world-cup" element={<Navigate to="/showcase?football=1" replace />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/company-registration" element={<CompanyRegistration />} />
            <Route path="/company-registration/success" element={<CompanyRegistrationSuccess />} />
            <Route path="/public-arena/:roomId" element={<ChessArenaRoom publicView />} />
            <Route 
              path="/admin/update-results" 
              element={
                <ProtectedRoute>
                  <AdminUpdateResults />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/social-posters" 
              element={
                <ProtectedRoute>
                  <AdminSocialPosters />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/missing-predictions" 
              element={
                <ProtectedRoute>
                  <AdminMissingPredictions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/agent-analytics" 
              element={
                <ProtectedRoute>
                  <AdminAgentAnalytics />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/football" 
              element={
                <ProtectedRoute>
                  <PublicFootballPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/formula1" 
              element={
                <ProtectedRoute>
                  <PublicFormula1Page />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/formula1/play/:raceId" 
              element={
                <ProtectedRoute>
                  <PublicFormula1PlayPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/prediction-board" 
              element={
                <ProtectedRoute>
                  <PredictionBoard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/leaderboard" 
              element={
                <ProtectedRoute>
                  <PublicLeaderboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/:publicTournamentId/leaderboard" 
              element={
                <ProtectedRoute>
                  <PublicLeaderboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/:publicTournamentId" 
              element={
                <ProtectedRoute>
                  <PublicHub />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/account" 
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/invitations" 
              element={
                <ProtectedRoute>
                  <Invitations />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payments" 
              element={
                <ProtectedRoute>
                  <PaymentsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/room/:roomId" 
              element={
                <ProtectedRoute>
                  <Room />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rankings/:roomId" 
              element={
                <ProtectedRoute>
                  <Rankings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/predictions/:roomId" 
              element={
                <ProtectedRoute>
                  <Predictions />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/room/:roomId/future-champion" 
              element={
                <ProtectedRoute>
                  <FutureChampionPlay />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/formula1/:roomId/playing"
              element={
                <ProtectedRoute>
                  <Formula1Playing />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/formula1/:roomId"
              element={
                <ProtectedRoute>
                  <Formula1Room />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/formula1-race/:roomId/:raceId" 
              element={
                <ProtectedRoute>
                  <Formula1Race />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chess/:roomId" 
              element={
                <ProtectedRoute>
                  <ChessRoom />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chess/:roomId/arena" 
              element={
                <ProtectedRoute>
                  <ChessArenaRoom />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chess/:roomId/arena/review/:gameId" 
              element={
                <ProtectedRoute>
                  <ChessArenaGameReview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chess-game/:gameId" 
              element={
                <ProtectedRoute>
                  <ChessGame />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </MainContent>
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
                backdropFilter: 'blur(12px)',
                borderRadius: 'var(--radius)',
              },
            }}
          />
        </div>
        </Router>
        </LanguageProvider>
      </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  )
}

export default App
